import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Typography, Chip, Tooltip, Button } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import { renderTaskStatus } from "../helpers";
import CopyableText from "../copyable-text/copyable-text";
import { LongRunningTaskModel } from "./types";

const renderNoParameters = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      height: "100%",
    }}
  >
    <Typography
      variant="body2"
      color="text.disabled"
      sx={{ fontSize: "0.875rem" }}
    >
      No parameters
    </Typography>
  </Box>
);

const renderParametersCell = (
  row: LongRunningTaskModel,
  onViewClick: (task: LongRunningTaskModel) => void,
) => {
  if (!row.task.jsonParameters) {
    return renderNoParameters();
  }

  // Check if parameters are not empty
  try {
    const parsed = JSON.parse(row.task.jsonParameters);
    const hasContent =
      parsed && typeof parsed === "object" && Object.keys(parsed).length > 0;

    if (!hasContent) {
      return renderNoParameters();
    }
  } catch {
    // If parsing fails, still show the View button to let user see raw data
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        height: "100%",
      }}
    >
      <Chip
        icon={<DescriptionIcon sx={{ fontSize: "1rem" }} />}
        label="View Details"
        size="small"
        color="primary"
        variant="outlined"
        onClick={() => onViewClick(row)}
        sx={{
          cursor: "pointer",
          fontWeight: 500,
          fontSize: "0.75rem",
          "&:hover": {
            backgroundColor: "primary.light",
            borderColor: "primary.main",
          },
        }}
      />
    </Box>
  );
};

const renderTaskIdCell = (params: GridRenderCellParams) => (
  <Tooltip title={params.value} placement="top">
    <Box sx={{ width: "100%" }}>
      <CopyableText text={params.value} />
    </Box>
  </Tooltip>
);

const renderDescriptionCell = (
  params: GridRenderCellParams<LongRunningTaskModel>,
) => (
  <Tooltip title={params.row?.task?.description || ""} placement="top">
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontSize: "0.875rem", fontWeight: 500 }}
      >
        {params.row?.task?.description || "—"}
      </Typography>
    </Box>
  </Tooltip>
);

const renderDateCell = (params: GridRenderCellParams) => (
  <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
    {params.value}
  </Typography>
);

const renderRunTimeCell = (params: GridRenderCellParams) => (
  <Typography
    variant="body2"
    sx={{
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "primary.main",
    }}
  >
    {params.value || "—"}
  </Typography>
);

const renderStatusCell = (params: GridRenderCellParams) => (
  <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
    {renderTaskStatus(params.value)}
  </Box>
);

export const columns: GridColDef[] = [
  {
    field: "id",
    headerName: "Task ID",
    flex: 1,
    minWidth: 170,
    valueGetter: (_value, row: LongRunningTaskModel) => row.task.id,
    renderCell: renderTaskIdCell,
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1.2,
    minWidth: 200,
    valueGetter: (_value, row: LongRunningTaskModel) => row.task.description,
    renderCell: renderDescriptionCell,
  },
  {
    field: "dateRequested",
    headerName: "Date Requested",
    flex: 0.8,
    minWidth: 140,
    valueGetter: (_value, row: LongRunningTaskModel) => row.task.dateRequested,
    renderCell: renderDateCell,
  },
  {
    field: "dateCompleted",
    headerName: "Date Completed",
    width: 250,
    valueGetter: (_value, row: LongRunningTaskModel) => row.task.dateCompleted,
    renderCell: renderDateCell,
  },
  {
    field: "tableName",
    headerName: "Table Name",
    width: 250,
    valueGetter: (_value, row: LongRunningTaskModel) =>
      row.tableInformation.name,
  },
  {
    field: "runTime",
    headerName: "Run Time",
    flex: 0.7,
    minWidth: 120,
    align: "center",
    headerAlign: "center",
    valueGetter: (_value, row: LongRunningTaskModel) => row.task.runTime,
    renderCell: renderRunTimeCell,
  },
];

export const createParametersColumn = (
  onViewClick: (task: LongRunningTaskModel) => void,
): GridColDef => ({
  field: "actions",
  headerName: "Parameters",
  flex: 0.7,
  minWidth: 125,
  sortable: false,
  filterable: false,
  align: "center",
  headerAlign: "left",
  renderCell: (params: GridRenderCellParams<LongRunningTaskModel>) =>
    renderParametersCell(params.row, onViewClick),
});

export const createCompleteTaskColumn = (
  onCompleteClick: (task: LongRunningTaskModel) => void,
  completingTaskId: string | null,
): GridColDef => ({
  field: "completeTask",
  headerName: "Actions",
  flex: 0.5,
  minWidth: 90,
  sortable: false,
  filterable: false,
  align: "center",
  headerAlign: "center",
  renderCell: (params: GridRenderCellParams<LongRunningTaskModel>) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
      }}
    >
      <Button
        size="small"
        variant="text"
        color="primary"
        disabled={
          params.row.task.status?.toLowerCase() === "completed" ||
          completingTaskId === params.row.task.id
        }
        onClick={() => onCompleteClick(params.row)}
        sx={{
          minWidth: "auto",
          p: 0,
          fontSize: "0.875rem",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": {
            backgroundColor: "transparent",
            textDecoration: "underline",
          },
        }}
      >
        {completingTaskId === params.row.task.id ? "Completing" : "Complete"}
      </Button>
    </Box>
  ),
});

export const statusColumn: GridColDef = {
  field: "status",
  headerName: "Status",
  flex: 0.7,
  minWidth: 125,
  align: "center",
  headerAlign: "center",
  valueGetter: (_value, row: LongRunningTaskModel) => row.task.status,
  renderCell: renderStatusCell,
};

export default columns;
